import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAIFeedbackMutation } from '@/app/lib/hooks/mutations/useAIFeedbackMutation';
import { Message } from '@/app/lib/stores/useCopilotStore';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: Message;
  sessionId: string;
}

const FEEDBACK_TYPES = [
  { value: 'incorrect_response', label: 'Incorrect Information', priority: 'high' },
  { value: 'bug', label: 'Technical Error', priority: 'critical' },
  { value: 'improvement', label: 'Not Helpful', priority: 'medium' },
  { value: 'feature_request', label: 'Missing Feature', priority: 'medium' },
  { value: 'other', label: 'Other Issue', priority: 'low' },
] as const;

export function FeedbackModal({ isOpen, onClose, message, sessionId }: FeedbackModalProps) {
  const [selectedType, setSelectedType] = useState<typeof FEEDBACK_TYPES[number] | null>(null);
  const [comment, setComment] = useState('');
  const { createFeedback } = useAIFeedbackMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedType) return;

    // Create a concise title based on the type
    const title = `${selectedType.label}: AI Response Issue`;
    
    // Build description with message content preview
    const messagePreview = message.content.substring(0, 200);
    const description = `User reported an issue with AI response.
Type: ${selectedType.label}
Message excerpt: "${messagePreview}${message.content.length > 200 ? '...' : ''}"`;

    await createFeedback.mutateAsync({
      sessionId,
      messageId: message.id,
      type: selectedType.value as any,
      priority: selectedType.priority as any,
      title,
      description,
      userComment: comment || undefined,
    });

    // Reset and close
    setSelectedType(null);
    setComment('');
    onClose();
  };

  const handleClose = () => {
    setSelectedType(null);
    setComment('');
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Report an Issue
                    </Dialog.Title>
                    
                    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                      {/* Issue Type Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          What's wrong with this response?
                        </label>
                        <div className="space-y-2">
                          {FEEDBACK_TYPES.map((type) => (
                            <label
                              key={type.value}
                              className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                            >
                              <input
                                type="radio"
                                name="feedbackType"
                                value={type.value}
                                checked={selectedType?.value === type.value}
                                onChange={() => setSelectedType(type)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-3 text-sm text-gray-700">{type.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Optional Comment */}
                      <div>
                        <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                          Additional details (optional)
                        </label>
                        <textarea
                          id="comment"
                          rows={3}
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          placeholder="Please provide any additional context that might help us understand the issue..."
                        />
                      </div>

                      {/* Actions */}
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                        <button
                          type="submit"
                          disabled={!selectedType || createFeedback.isPending}
                          className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                        >
                          {createFeedback.isPending ? 'Submitting...' : 'Submit Report'}
                        </button>
                        <button
                          type="button"
                          onClick={handleClose}
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}